import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation } from "wouter"
import type { Form, FormField } from "shared"
import { pathHelpers } from "../../../paths"
import {
  buildSubmission,
  buildSteps,
  type Answers,
} from "../utils/feedbackForm"
import { Button } from "../../../components/Button"
import { SafeExitButton } from "../../../components/SafeExitButton"
import { FormStepper } from "../components/FormStepper"
import { FeedbackFormSkeleton } from "../components/FeedbackFormSkeleton"
import { RatingField } from "../components/RatingField"
import { PictogramRatingField } from "../components/PictogramRatingField"
import { TextAreaField } from "../components/TextAreaField"
import { CheckboxField } from "../components/CheckboxField"
import { postFeedback, fetchFeedbackForm } from "../api"
import {
  ApiResponseError,
  isExpectedApiError,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import { useFocus } from "../../../hooks/useFocus"
import { invitationQueryKey } from "../utils/invitationQueryKey"
import styles from "./FeedbackFormPage.module.scss"

type Props = {
  invitationId: string
  kioskMode?: boolean
}

export function FeedbackFormPage({ invitationId, kioskMode }: Props) {
  const [, navigate] = useLocation()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<Form | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>({})
  const [error, setError] = useState<string | null>(null)
  const fieldRef = useFocus<HTMLDivElement>(currentStep)

  useEffect(() => {
    fetchFeedbackForm(invitationId)
      .then(setForm)
      .catch((err) => {
        trackError(err, { source: "fetchFeedbackForm" })
        setLoadError(true)
      })
  }, [invitationId])

  if (loadError) {
    return (
      <>
        <h1 className={styles.pageHeading}>Give your feedback</h1>
        <div className={styles.container}>
          <div className={styles.card}>
            <p className={styles.error} role="alert">
              Something went wrong, please try again.
            </p>
          </div>
        </div>
        {!kioskMode && <SafeExitButton />}
      </>
    )
  }

  if (!form) return <FeedbackFormSkeleton />

  const fields = form.fields
  const steps = buildSteps(fields)
  const currentStepFields = steps[currentStep]
  const isLastStep = currentStep === steps.length - 1
  const isDisabled = currentStepFields.some(
    (field) => !!field.required && answers[field.id] === undefined,
  )

  function handleAnswer(field: FormField, value: string | number | boolean) {
    setAnswers((prev) => ({ ...prev, [field.id]: value }))
  }

  async function handleNext() {
    // Next is aria-disabled (focusable + announced) rather than natively disabled,
    // so the guard here is what actually blocks advancing/submitting.
    if (isDisabled) return
    if (!isLastStep) {
      setCurrentStep((s) => s + 1)
      return
    }

    const body = buildSubmission(fields, answers, invitationId)
    try {
      await postFeedback(body)
      // Used now: going back to it must ask again and find it so, not draw the
      // form from the gate's answer for the visit.
      queryClient.removeQueries({ queryKey: invitationQueryKey(invitationId) })
      navigate(pathHelpers.toThankYouPath(!!kioskMode))
    } catch (err) {
      if (!isExpectedApiError(err)) trackError(err, { source: "postFeedback" })
      setError(
        err instanceof ApiResponseError && err.statusCode === 429
          ? "Too many requests. Please wait a moment and try again."
          : isExpectedApiError(err)
            ? err.message
            : "Something went wrong, please try again.",
      )
    }
  }

  return (
    <>
      <h1 className={styles.pageHeading}>Give your feedback</h1>
      <div className={styles.container}>
        <FormStepper stepCount={steps.length} currentStep={currentStep} />
        <div className={styles.card}>
          <div className={styles.field} ref={fieldRef}>
            {currentStepFields.map((field) => (
              <div key={field.id}>
                {field.type === "rating" && (
                  <RatingField
                    field={field}
                    value={answers[field.id] as number | undefined}
                    onChange={(value) => handleAnswer(field, value)}
                  />
                )}
                {field.type === "rating-pictogram" && (
                  <PictogramRatingField
                    field={field}
                    value={answers[field.id] as number | undefined}
                    onChange={(value) => handleAnswer(field, value)}
                  />
                )}
                {field.type === "textarea" && (
                  <TextAreaField
                    field={field}
                    value={String(answers[field.id] ?? "")}
                    onChange={(value) => handleAnswer(field, value)}
                  />
                )}
                {field.type === "checkbox" && (
                  <CheckboxField
                    field={field}
                    value={Boolean(answers[field.id])}
                    onChange={(value) => handleAnswer(field, value)}
                  />
                )}
              </div>
            ))}
          </div>

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <div className={styles.nav}>
            {currentStep === 0 && (
              <Button variant="secondary" onClick={() => window.history.back()}>
                Back
              </Button>
            )}
            {currentStep > 0 && (
              <Button
                variant="secondary"
                onClick={() => setCurrentStep((s) => s - 1)}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              ariaDisabled={isDisabled}
              describedBy={isDisabled ? "next-hint" : undefined}
            >
              {isLastStep ? "Send" : "Next"}
            </Button>
            {isDisabled && (
              <span id="next-hint" className={styles.srOnly}>
                Please select an option to continue.
              </span>
            )}
          </div>
        </div>
      </div>
      {!kioskMode && <SafeExitButton />}
    </>
  )
}
